import { useLayoutEffect, useRef, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { generatePath, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../../routes/routeConstants/appRoutes';
import './MapListView.scss';

interface MapRecord {
  id: string;
  createdAt: string | Date;
  general: {
    mapTitle: string;
    mapDescription: string;
  };
  permissions: {
    users: { name: string; checked: boolean }[];
  };
}

interface MapListViewProps {
  maps: MapRecord[];
}

const HEADER_FALLBACK_PX = 47;

const getCreator = (users: { name: string; checked: boolean }[]) => {
  const creator = users.find((user) => user.checked);
  return creator ? creator.name : '';
};

const stripHtml = (html?: string) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').trim();
};

const columns: ColumnsType<MapRecord> = [
  {
    title: 'NAME',
    dataIndex: ['general', 'mapTitle'],
    key: 'mapTitle',
    ellipsis: true,
    render: (value: string) => {
      const text = stripHtml(value);
      return text ? <span className="data-table__name">{text}</span> : <span className="data-table__muted">Untitled</span>;
    },
  },
  {
    title: 'DESCRIPTION',
    dataIndex: ['general', 'mapDescription'],
    key: 'mapDescription',
    ellipsis: true,
    render: (value: string) => {
      const text = stripHtml(value);
      return text ? <span className="data-table__desc">{text}</span> : <span className="data-table__muted">—</span>;
    },
  },
  {
    title: 'CREATOR',
    key: 'creator',
    width: 140,
    ellipsis: true,
    render: (_, record) => getCreator(record.permissions?.users || []) || '—',
  },
  {
    title: 'CREATED AT',
    key: 'createdAt',
    width: 180,
    render: (_, record) => (record.createdAt ? moment(record.createdAt).format('MMM. DD, YYYY HH:mm') : '—'),
  },
];

const MapListView: React.FC<MapListViewProps> = ({ maps }) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [bodyScrollY, setBodyScrollY] = useState(320);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const headerEl = el.querySelector('.ant-table-header') as HTMLElement | null;
      const headerH = headerEl?.getBoundingClientRect().height || HEADER_FALLBACK_PX;
      // scroll.y is the body viewport only; header stays fixed above it.
      const next = Math.max(120, Math.floor(el.clientHeight - headerH));
      setBodyScrollY((prev) => (prev === next ? prev : next));
    };

    measure();
    // Second pass after Ant mounts the split header/body tables.
    const raf = requestAnimationFrame(measure);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [maps.length]);

  return (
    <div className="data-content" ref={containerRef}>
      <Table<MapRecord>
        dataSource={maps}
        columns={columns}
        rowKey="id"
        pagination={false}
        className="data-table"
        tableLayout="fixed"
        scroll={{ y: bodyScrollY, x: true }}
        onRow={(record) => ({
          onClick: () => {
            navigate(generatePath(AppRoutes.MAP_VIEW, { id: record.id }));
          },
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

export default MapListView;
